import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";
import {
  ChevronLeft,
  Plus,
  Trash2,
  Edit2,
  X,
  Receipt,
  Fuel,
  Wrench,
  Shield,
  FileText,
  MoreHorizontal,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const CATEGORIES = [
  { value: "all", label: "All" },
  { value: "fuel", label: "Fuel" },
  { value: "maintenance", label: "Maintenance" },
  { value: "parts", label: "Parts" },
  { value: "insurance", label: "Insurance" },
  { value: "registration", label: "Registration" },
  { value: "other", label: "Other" },
];

const CATEGORY_COLORS = {
  fuel: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  maintenance: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  parts: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  insurance: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  registration: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  other: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
};

const detailMsg = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && d.message) return String(d.message);
  if (e?.message) return e.message;
  return fallback;
};

export default function Receipts() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [receipts, setReceipts] = useState([]);
  const [summary, setSummary] = useState({ this_month: 0, year_to_date: 0 });
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [deleting, setDeleting] = useState(null);

  // Form state
  const [formTitle, setFormTitle] = useState("");
  const [formAmount, setFormAmount] = useState("");
  const [formCategory, setFormCategory] = useState("fuel");
  const [formDate, setFormDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [formNotes, setFormNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const safeBack = () => window.history.length > 2 ? navigate(-1) : navigate("/owner", { replace: true });

  const loadReceipts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeCategory !== "all") params.set("category", activeCategory);
      const [rRes, sRes] = await Promise.all([
        api.get(`/owner/receipts?${params.toString()}`),
        api.get("/owner/receipts/summary"),
      ]);
      setReceipts(rRes.data?.receipts || rRes.data || []);
      setSummary(sRes.data || { this_month: 0, year_to_date: 0 });
    } catch (e) {
      if (e?.response?.status === 403 && e?.response?.data?.upgrade_required) {
        navigate("/owner/upgrade", { replace: true });
        return;
      }
      toast.error(detailMsg(e, "Failed to load receipts"));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadReceipts(); }, [activeCategory]);

  const resetForm = () => {
    setFormTitle("");
    setFormAmount("");
    setFormCategory("fuel");
    setFormDate(new Date().toISOString().slice(0, 10));
    setFormNotes("");
    setEditingReceipt(null);
  };

  const openAddForm = () => {
    resetForm();
    setFormOpen(true);
  };

  const openEditForm = (receipt) => {
    setEditingReceipt(receipt);
    setFormTitle(receipt.title || "");
    setFormAmount(String(receipt.amount || ""));
    setFormCategory(receipt.category || "other");
    setFormDate(receipt.date ? receipt.date.slice(0, 10) : new Date().toISOString().slice(0, 10));
    setFormNotes(receipt.notes || "");
    setFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formTitle.trim()) { toast.error("Title is required"); return; }
    if (!formAmount || parseFloat(formAmount) <= 0) { toast.error("Enter a valid amount"); return; }

    setSaving(true);
    const payload = {
      title: formTitle.trim(),
      amount: parseFloat(formAmount),
      category: formCategory,
      date: formDate,
      notes: formNotes.trim() || null,
    };

    try {
      if (editingReceipt) {
        await api.put(`/owner/receipts/${editingReceipt.receipt_id || editingReceipt.id}`, payload);
        toast.success("Receipt updated");
      } else {
        await api.post("/owner/receipts", payload);
        toast.success("Receipt added");
      }
      setFormOpen(false);
      resetForm();
      await loadReceipts();
    } catch (e) {
      if (e?.response?.status === 403 && e?.response?.data?.upgrade_required) {
        navigate("/owner/upgrade", { replace: true });
        return;
      }
      toast.error(detailMsg(e, "Failed to save receipt"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (receiptId) => {
    if (!window.confirm("Delete this receipt?")) return;
    setDeleting(receiptId);
    try {
      await api.delete(`/owner/receipts/${receiptId}`);
      toast.success("Receipt deleted");
      setExpandedId(null);
      await loadReceipts();
    } catch (e) {
      toast.error(detailMsg(e, "Delete failed"));
    } finally {
      setDeleting(null);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar />

      {/* Header */}
      <div className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={safeBack} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ChevronLeft className="w-5 h-5" /> Back
          </button>
          <div className="font-display text-sm tracking-widest text-zinc-500">RECEIPTS</div>
          <div className="w-12" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Summary bar */}
        <div className="border border-white/10 bg-[#121214] p-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-6">
            <div>
              <div className="label-eyebrow text-zinc-500 text-xs">THIS MONTH</div>
              <div className="font-heading text-xl font-bold text-white">${Number(summary.this_month || 0).toFixed(2)}</div>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div>
              <div className="label-eyebrow text-zinc-500 text-xs">YEAR TO DATE</div>
              <div className="font-heading text-xl font-bold text-white">${Number(summary.year_to_date || 0).toFixed(2)}</div>
            </div>
          </div>
          <button onClick={openAddForm} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Receipt
          </button>
        </div>

        {/* Category filter tabs */}
        <div className="flex gap-1 overflow-x-auto pb-1 -mx-2 px-2">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setActiveCategory(cat.value)}
              className={`px-4 py-2 text-xs font-display tracking-widest uppercase whitespace-nowrap transition-colors border ${
                activeCategory === cat.value
                  ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                  : "bg-transparent border-white/10 text-zinc-400 hover:text-white hover:border-white/20"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Inline add/edit form */}
        {formOpen && (
          <div className="border border-white/10 bg-[#121214] p-6 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow text-zinc-500">
                {editingReceipt ? "EDIT RECEIPT" : "NEW RECEIPT"}
              </div>
              <button onClick={() => { setFormOpen(false); resetForm(); }} className="text-zinc-500 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label-eyebrow text-zinc-500 text-xs">TITLE</label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="e.g., Shell gas station"
                  className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white"
                  required
                  maxLength={100}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="label-eyebrow text-zinc-500 text-xs">AMOUNT ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formAmount}
                    onChange={(e) => setFormAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="label-eyebrow text-zinc-500 text-xs">CATEGORY</label>
                  <select
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value)}
                    className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white"
                  >
                    {CATEGORIES.filter((c) => c.value !== "all").map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label-eyebrow text-zinc-500 text-xs">DATE</label>
                  <input
                    type="date"
                    value={formDate}
                    onChange={(e) => setFormDate(e.target.value)}
                    max={new Date().toISOString().slice(0, 10)}
                    className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="label-eyebrow text-zinc-500 text-xs">NOTES (OPTIONAL)</label>
                <textarea
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder="Any additional details..."
                  className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white h-20 resize-none"
                  maxLength={500}
                />
              </div>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary w-full py-3 disabled:opacity-50"
              >
                {saving ? "SAVING..." : editingReceipt ? "UPDATE RECEIPT" : "ADD RECEIPT"}
              </button>
            </form>
          </div>
        )}

        {/* Receipt list */}
        {loading ? (
          <div className="text-zinc-500 text-center py-12 label-eyebrow">LOADING...</div>
        ) : receipts.length === 0 ? (
          <div className="border border-dashed border-white/10 p-12 text-center">
            <Receipt className="w-10 h-10 text-zinc-700 mx-auto mb-4" strokeWidth={1.2} />
            <div className="label-eyebrow mb-3 text-zinc-500">NO RECEIPTS YET</div>
            <p className="text-zinc-400 mb-6">Start tracking your car expenses by adding your first receipt.</p>
            <button onClick={openAddForm} className="btn-primary inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Receipt
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {receipts.map((r) => {
              const id = r.receipt_id || r.id;
              const isExpanded = expandedId === id;
              const catColor = CATEGORY_COLORS[r.category] || CATEGORY_COLORS.other;
              return (
                <div key={id} className="border border-white/10 bg-[#121214] overflow-hidden">
                  {/* Row */}
                  <button
                    onClick={() => toggleExpand(id)}
                    className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-semibold text-white truncate">{r.title}</span>
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${catColor}`}>
                          {r.category}
                        </span>
                      </div>
                      <div className="text-xs text-zinc-500">
                        {r.date ? new Date(r.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-heading text-lg font-bold text-white">${Number(r.amount || 0).toFixed(2)}</span>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="border-t border-white/10 px-5 py-4 space-y-3">
                      {r.notes && (
                        <div>
                          <div className="label-eyebrow text-zinc-500 text-xs mb-1">NOTES</div>
                          <p className="text-sm text-zinc-300">{r.notes}</p>
                        </div>
                      )}
                      <div className="flex gap-3">
                        <button
                          onClick={() => openEditForm(r)}
                          className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5" /> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(id)}
                          disabled={deleting === id}
                          className="flex items-center gap-1.5 text-sm text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> {deleting === id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
